'use client';

import React, { forwardRef, useImperativeHandle } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';

export interface HorizontalScrollerHandle {
  scrollToCenter: (element: HTMLElement) => void;
  scrollToStart: () => void;
  /** Scroll one `scrollAmount` step — for driving the scroller from your own controls */
  scrollBy: (direction: 'left' | 'right') => void;
  getContainerElement: () => HTMLDivElement | null;
}

export interface HorizontalScrollerProps {
  children: React.ReactNode;
  scrollAmount?: number;
  gradientFromColor?: 'white' | 'neutral-50';
  ariaLabelLeft?: string;
  ariaLabelRight?: string;
  testIdPrefix?: string;
  className?: string;
  containerClassName?: string;
  /**
   * Render the built-in arrow buttons over the fade edges. Set to `false` when
   * the consumer supplies its own controls (drive them with the `scrollBy`
   * handle) — the fade edges still render, just as non-interactive gradients.
   * @default true
   */
  showArrows?: boolean;
}

const gradientColors = {
  white: {
    left: 'bg-gradient-to-r from-white via-white to-transparent',
    right: 'bg-gradient-to-l from-white via-white to-transparent',
  },
  'neutral-50': {
    left: 'bg-gradient-to-r from-neutral-50 via-neutral-50 to-transparent',
    right: 'bg-gradient-to-l from-neutral-50 via-neutral-50 to-transparent',
  },
};

const HorizontalScroller = forwardRef<
  HorizontalScrollerHandle,
  HorizontalScrollerProps
>(
  (
    {
      children,
      scrollAmount = 150,
      gradientFromColor = 'white',
      ariaLabelLeft = 'Scroll left',
      ariaLabelRight = 'Scroll right',
      testIdPrefix = 'horizontal-scroller',
      className,
      containerClassName,
      showArrows = true,
    },
    ref
  ) => {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);

    const scroll = React.useCallback(
      (direction: 'left' | 'right') => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({
          left: direction === 'left' ? -scrollAmount : scrollAmount,
          behavior: 'smooth',
        });
      },
      [scrollAmount]
    );

    useImperativeHandle(ref, () => ({
      scrollToCenter: (element: HTMLElement) => {
        const container = scrollRef.current;
        if (!container) return;
        const targetScroll =
          element.offsetLeft -
          container.clientWidth / 2 +
          element.offsetWidth / 2;
        container.scrollTo({ left: targetScroll, behavior: 'smooth' });
      },
      scrollToStart: () => {
        scrollRef.current?.scrollTo({ left: 0 });
      },
      scrollBy: scroll,
      getContainerElement: () => scrollRef.current,
    }));

    const updateScrollState = React.useCallback(() => {
      const el = scrollRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    React.useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      updateScrollState();
      el.addEventListener('scroll', updateScrollState, { passive: true });
      const observer = new ResizeObserver(updateScrollState);
      observer.observe(el);
      return () => {
        el.removeEventListener('scroll', updateScrollState);
        observer.disconnect();
      };
    }, [updateScrollState]);

    const gradient = gradientColors[gradientFromColor];

    return (
      <div
        className={`relative flex items-center overflow-hidden ${containerClassName ?? ''}`}
        data-testid={`${testIdPrefix}-container`}
      >
        {canScrollLeft &&
          (showArrows ? (
            <button
              onClick={() => scroll('left')}
              className={`absolute left-0 z-10 flex items-center justify-center w-8 h-full cursor-pointer ${gradient.left}`}
              aria-label={ariaLabelLeft}
              data-testid={`${testIdPrefix}-scroll-left`}
            >
              <Icon icon={faChevronLeft} className="w-3 h-3 text-neutral-500" />
            </button>
          ) : (
            <div
              aria-hidden="true"
              className={`absolute left-0 z-10 w-8 h-full pointer-events-none ${gradient.left}`}
              data-testid={`${testIdPrefix}-fade-left`}
            />
          ))}
        <div
          ref={scrollRef}
          className={`flex flex-1 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] ${className ?? ''}`}
        >
          {children}
        </div>
        {canScrollRight &&
          (showArrows ? (
            <button
              onClick={() => scroll('right')}
              className={`absolute right-0 z-10 flex items-center justify-center w-8 h-full cursor-pointer ${gradient.right}`}
              aria-label={ariaLabelRight}
              data-testid={`${testIdPrefix}-scroll-right`}
            >
              <Icon
                icon={faChevronRight}
                className="w-3 h-3 text-neutral-500"
              />
            </button>
          ) : (
            <div
              aria-hidden="true"
              className={`absolute right-0 z-10 w-8 h-full pointer-events-none ${gradient.right}`}
              data-testid={`${testIdPrefix}-fade-right`}
            />
          ))}
      </div>
    );
  }
);

HorizontalScroller.displayName = 'HorizontalScroller';

export default HorizontalScroller;
